import React, {
  useEffect,
  useState,
  useMemo,
  FC,
  Dispatch,
  SetStateAction,
} from "react";
import { Link } from "react-router-dom";

import { Button, Col, Row, Spinner } from "reactstrap";
import DataTable from "../../shared/base-components/DataTable";
import { useExpenseCategoryListMutation } from "./Expense.actions";
import ExpenseCategoryModal from "./ExpenseCategoryModal";
import { MODAL_TYPES, Permissions } from "../../shared/models/Types";
import ExpenseCategoryDeleteModal from "./ExpenseCategoryDeleteModal";
import { formatDecimal } from "../../shared/utils/formatText";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../shared/actions/Authentication.actions";

export interface ExpenseCategoryListProps {
  setStep: Dispatch<
    SetStateAction<{
      step: number;
      id: string;
      lastId: string;
      name: string;
    }>
  >;
}

const ExpenseCategoryList: FC<ExpenseCategoryListProps> = ({ setStep }) => {
  document.title =
    "Gider Kategorileri | Bu ekranda alınacak ücretli listesini görebilirsiniz.";

  const permissionList = useRecoilValue(permissionsSelector);

  const { data, mutate, isLoading } = useExpenseCategoryListMutation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalOptions, setModalOptions] = useState<{
    data?: any;
    modalType?: string;
  }>({
    data: undefined,
    modalType: undefined,
  });

  const toggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const modalOptionsHandler = (data?: any, modalType?: string) => {
    setModalOptions({ data: data, modalType: modalType });
  };

  const addPermission = permissionList[Permissions.AddCategory];
  const updatePermission = permissionList[Permissions.ChangeCategory];
  const viewPermission = permissionList[Permissions.ViewCategory];
  const deletePermission = permissionList[Permissions.DeleteCategory];

  const columns = useMemo(
    () => [
      {
        Header: "Kategori Adı",
        filterable: false,
        disableFilters: true,
        accessor: "name",
        Cell: (response: any) => (
          <>
            <Link
              to="#"
              onClick={() =>
                setStep({
                  step: 1,
                  id: response.row.original.id,
                  lastId: response.row.original.id,
                  name: response.row.original.name,
                })
              }
              className="text-dark"
            >
              <h5 className="font-size-14 mb-1">
                {response.row.original.name}
              </h5>
            </Link>
          </>
        ),
      },
      {
        Header: "Toplam Gider",
        filterable: false,
        disableFilters: true,
        accessor: "total_cost",
        Cell: (response: any) => (
          <>
            <h5 className="font-size-14 mb-1">
              {!!response.row.original.total_cost
                ? formatDecimal(response.row.original.total_cost) + " ₺"
                : 0}
            </h5>
          </>
        ),
      },
      {
        Header: "İşlemler",
        Cell: (cellProps: any) => {
          return (
            <div className="d-flex gap-3">
              {updatePermission && (
                <Link className="text-warning" to="#">
                  <i
                    className="mdi mdi-pencil font-size-18"
                    id="edittooltip"
                    onClick={() => {
                      const userData = cellProps.row.original;

                      toggle();
                      modalOptionsHandler(userData, MODAL_TYPES.UPDATE);
                    }}
                  ></i>
                </Link>
              )}

              {deletePermission && (
                <Link className="text-danger" to="#">
                  <i
                    className="mdi mdi-delete font-size-18"
                    id="deletetooltip"
                    onClick={() => {
                      const userData = cellProps.row.original;

                      toggle();
                      modalOptionsHandler(userData, MODAL_TYPES.DELETE);
                    }}
                  ></i>
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    mutate({});
  }, []);

  return (
    <React.Fragment>
      <Row className="align-items-center">
        <Col md={6}>
          <div className="mb-3">
            <h5 className="card-title">
              Gider Kategorisi
              <span className="text-muted fw-normal ms-2">
                {isLoading ? (
                  <Spinner className="ms-1" color="primary" />
                ) : (
                  `(${data?.length})`
                )}
              </span>
            </h5>
          </div>
        </Col>

        <Col md={6}>
          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
            <div>
              <ul className="nav nav-pills"></ul>
            </div>
            <div>
              {addPermission && (
                <Button
                  onClick={() => {
                    modalOptionsHandler(data, MODAL_TYPES.CREATE);
                    toggle();
                  }}
                  className="btn btn-light"
                >
                  <i className="bx bx-plus me-1"></i> Kategori Ekle
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="12">
          {data?.length && viewPermission ? (
            <DataTable
              columns={columns}
              data={data}
              customPageSize={10}
              className="table align-middle datatable dt-responsive table-check nowrap"
            />
          ) : (
            !isLoading && <>Herhangi bir gider kategorisi bulunamadı.</>
          )}
        </Col>
      </Row>

      {isModalOpen &&
        updatePermission &&
        modalOptions.modalType !== MODAL_TYPES.DELETE && (
          <ExpenseCategoryModal
            categoryData={modalOptions.data}
            modalType={modalOptions.modalType}
            isModalOpen={isModalOpen}
            toggle={toggle}
            mutate={mutate}
          />
        )}

      {isModalOpen &&
        deletePermission &&
        modalOptions.modalType === MODAL_TYPES.DELETE && (
          <ExpenseCategoryDeleteModal
            userData={modalOptions.data}
            modalType={modalOptions.modalType}
            isModalOpen={isModalOpen}
            toggle={toggle}
            mutate={mutate}
          />
        )}
    </React.Fragment>
  );
};

export default ExpenseCategoryList;
